import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, X, CalendarClock } from 'lucide-react';
import { api } from '../../api/client';

/**
 * Horario de la pasada completa del Monitor de Precios por empresa (2026-10-12, pedido del dueño):
 * días de la semana + horas fijas en hora de Miami (Nueva York). Guarda en el director de turnos
 * (PUT /price-monitor/conductor/seller/:id). El director arranca la pasada a esa hora o, si otra
 * empresa está usando el turno, apenas se libera.
 */

export const BUSINESS_TZ = 'America/New_York';
const DAY_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const WEEKDAYS = [1, 2, 3, 4, 5];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

export interface SellerSchedule {
  seller_id: number;
  slices: number;
  at_hours: number[];
  days: number[];
  every_minutes: number | null;
  next_at: string | null;
  pending_slot: string | null;
  done_slot: string | null;
  in_progress: { slot: string; next_slice: number } | null;
  last_pass_started_at: string | null;
}

interface ConductorSnapshot {
  settings: { enabled: boolean; gap_minutes: number; ring: Array<{ seller_id: number; slices: number; at_hours?: number[]; days?: number[]; every_minutes?: number }> };
  schedule?: SellerSchedule[];
}

export const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

export function daysLabel(days: number[]): string {
  if (!days.length || days.length === 7) return 'todos los días';
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.join(',') === WEEKDAYS.join(',')) return 'lunes a viernes';
  if (sorted.join(',') === '0,6') return 'sábados y domingos';
  return sorted.map((d) => DAY_SHORT[d]).join(', ');
}

/** Resumen corto para la fila del proceso: "08:00 · todos los días" o "en rueda, cada 8 h". */
export function scheduleSummary(s: SellerSchedule | undefined | null): string | null {
  if (!s) return null;
  if (s.at_hours.length) return `${s.at_hours.map(hh).join(' · ')} · ${daysLabel(s.days)}`;
  if (s.every_minutes) return `en rueda, cada ${Math.round(s.every_minutes / 60)} h`;
  return 'en rueda con las demás empresas';
}

export function formatMiami(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('es', {
    timeZone: BUSINESS_TZ,
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/** Busca la entrada de la empresa en la respuesta del director (con o sin `schedule`). */
export function pickSchedule(snap: ConductorSnapshot | null | undefined, sellerId: number): SellerSchedule | null {
  if (!snap) return null;
  const fromSchedule = snap.schedule?.find((s) => s.seller_id === sellerId);
  if (fromSchedule) return fromSchedule;
  const r = snap.settings?.ring?.find((x) => x.seller_id === sellerId);
  if (!r) return null;
  return {
    seller_id: sellerId,
    slices: r.slices,
    at_hours: r.at_hours ?? [],
    days: r.days ?? [],
    every_minutes: r.every_minutes ?? null,
    next_at: null,
    pending_slot: null,
    done_slot: null,
    in_progress: null,
    last_pass_started_at: null,
  };
}

interface Props {
  sellerId: number;
  canEdit: boolean;
  processEnabled: boolean;
  showToast: (msg: string, ok: boolean) => void;
  onSaved?: () => void;
}

export default function PmScheduleEditor({ sellerId, canEdit, processEnabled, showToast, onSaved }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState<SellerSchedule | null>(null);
  const [conductorOn, setConductorOn] = useState(true);
  const [hours, setHours] = useState<number[]>([]);
  const [days, setDays] = useState<number[]>(ALL_DAYS);
  const [newHour, setNewHour] = useState<number>(8);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<ConductorSnapshot>('/price-monitor/conductor');
      if (res.success && res.data) {
        const snap = res.data as unknown as ConductorSnapshot;
        setConductorOn(snap.settings?.enabled !== false);
        const s = pickSchedule(snap, sellerId);
        setCurrent(s);
        setHours(s?.at_hours ?? []);
        setDays(s?.days?.length ? s.days : ALL_DAYS);
      } else {
        setError((res as any).error || 'No se pudo leer el horario');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer el horario');
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    const curHours = [...(current?.at_hours ?? [])].sort((a, b) => a - b).join(',');
    const curDays = (current?.days?.length ? [...current.days] : ALL_DAYS).sort((a, b) => a - b).join(',');
    return (
      [...hours].sort((a, b) => a - b).join(',') !== curHours ||
      [...days].sort((a, b) => a - b).join(',') !== curDays
    );
  }, [current, hours, days]);

  const toggleDay = (d: number) =>
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)));
  const addHour = () => {
    if (hours.includes(newHour)) return;
    setHours((prev) => [...prev, newHour].sort((a, b) => a - b));
  };
  const removeHour = (h: number) => setHours((prev) => prev.filter((x) => x !== h));

  const save = async () => {
    if (!hours.length) { showToast('Elegí al menos una hora', false); return; }
    if (!days.length) { showToast('Elegí al menos un día', false); return; }
    setSaving(true);
    try {
      // Se mandan los días tal cual (los 7 marcados = sin restricción; el servidor lo normaliza).
      const res = await api.put(`/price-monitor/conductor/seller/${sellerId}`, {
        at_hours: hours,
        days,
      });
      if (res.success) {
        showToast('Horario guardado', true);
        await load();
        onSaved?.();
      } else {
        showToast((res as any).error || 'No se pudo guardar el horario', false);
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'No se pudo guardar el horario', false);
    } finally {
      setSaving(false);
    }
  };

  const labelCls = 'block text-xs font-medium text-[var(--color-text-secondary)] mb-1';
  const helpCls = 'text-[10px] text-[var(--color-text-tertiary)] mt-0.5';
  const chipBase = 'px-2.5 py-1 rounded-full text-xs border transition-colors';
  const chipOn = 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]';
  const chipOff = 'bg-[var(--color-bg)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]';

  if (loading) {
    return (
      <div className="mb-4 p-3 rounded-lg border border-[var(--color-border)] flex items-center gap-2 text-xs text-[var(--color-text-tertiary)]">
        <Loader2 size={14} className="animate-spin" /> Leyendo el horario…
      </div>
    );
  }

  const timesPerDay = hours.length;
  const inRing = !current?.at_hours?.length;

  return (
    <div className="mb-4 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
      <div className="flex items-center gap-2 mb-2">
        <CalendarClock size={14} className="text-[var(--color-primary)]" />
        <h5 className="text-xs font-semibold text-[var(--color-text)]">
          Horario de la pasada completa <span className="font-normal text-[var(--color-text-tertiary)]">(hora de Miami)</span>
        </h5>
      </div>

      {error && <p className="text-xs text-red-400 mb-2">{error}</p>}

      {!conductorOn && (
        <p className="text-xs text-amber-500 mb-2">
          El director de turnos está apagado: las pasadas vuelven a la cadencia por intervalo y este horario no se aplica.
        </p>
      )}
      {!processEnabled && (
        <p className="text-xs text-amber-500 mb-2">
          El Monitor está apagado para esta empresa (interruptor de la fila). Con el horario guardado, no corre hasta encenderlo.
        </p>
      )}
      {inRing && (
        <p className="text-xs text-amber-500 mb-2">
          Hoy esta empresa corre en rueda con las demás{current?.every_minutes ? `, cada ${Math.round(current.every_minutes / 60)} h aprox.` : ''}. Elegí días y horas y guardá para fijarle horario.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Días en que corre</label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_DAYS.map((d) => (
              <button
                key={d}
                type="button"
                disabled={!canEdit}
                onClick={() => toggleDay(d)}
                className={`${chipBase} ${days.includes(d) ? chipOn : chipOff} ${!canEdit ? 'opacity-70 cursor-default' : ''}`}
              >
                {DAY_SHORT[d]}
              </button>
            ))}
          </div>
          {canEdit && (
            <div className="flex gap-3 mt-1.5">
              <button type="button" className="text-[10px] underline text-[var(--color-text-tertiary)]" onClick={() => setDays(ALL_DAYS)}>Todos los días</button>
              <button type="button" className="text-[10px] underline text-[var(--color-text-tertiary)]" onClick={() => setDays(WEEKDAYS)}>Lunes a viernes</button>
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>Horas de arranque ({timesPerDay === 1 ? '1 vez por día' : `${timesPerDay} veces por día`})</label>
          <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
            {hours.length === 0 && <span className="text-xs text-[var(--color-text-tertiary)]">Sin horas elegidas</span>}
            {hours.map((h) => (
              <span key={h} className={`${chipBase} ${chipOn} inline-flex items-center gap-1`}>
                {hh(h)}
                {canEdit && (
                  <button type="button" onClick={() => removeHour(h)} className="hover:opacity-70" aria-label={`Quitar ${hh(h)}`}>
                    <X size={12} />
                  </button>
                )}
              </span>
            ))}
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <select
                value={newHour}
                onChange={(e) => setNewHour(parseInt(e.target.value))}
                className="px-2 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-xs"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i} disabled={hours.includes(i)}>{hh(i)}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={addHour}
                disabled={hours.includes(newHour)}
                className="px-2.5 py-1.5 text-xs rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Plus size={12} /> Agregar hora
              </button>
            </div>
          )}
          <p className={helpCls}>
            Si a esa hora otra empresa está usando el turno, la pasada arranca apenas se libera. Si el sistema estuvo apagado más de 3 h a esa hora, ese horario se saltea.
          </p>
        </div>
      </div>

      {current && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-text-secondary)]">
          {current.in_progress ? (
            <span className="text-blue-400">
              Pasada en curso ({current.in_progress.next_slice > current.slices ? 'última tanda' : `tanda ${current.in_progress.next_slice - 1} de ${current.slices}`})
            </span>
          ) : current.pending_slot ? (
            <span className="text-amber-500">Horario de las {hh(parseInt(current.pending_slot.slice(11, 13)))} esperando que se libere el turno</span>
          ) : null}
          {current.at_hours.length > 0 && (
            <span>Próxima pasada: <b>{formatMiami(current.next_at)}</b> (Miami)</span>
          )}
          {current.last_pass_started_at && (
            <span>Última pasada: {formatMiami(current.last_pass_started_at)}</span>
          )}
          {current.slices > 1 && <span>Se hace en {current.slices} tandas seguidas</span>}
        </div>
      )}

      {canEdit && (
        <div className="mt-3">
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty || !hours.length || !days.length}
            className="px-3 py-1.5 text-xs rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : null}
            Guardar horario
          </button>
        </div>
      )}
    </div>
  );
}
